/* eslint-disable react/no-children-prop */
import React from "react";
import { getRouteApi } from "@tanstack/react-router";
import { useTranslation, Trans } from "react-i18next";
import { DateTime } from "luxon";
// IUI
import {
  Box,
  Container,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  ThemeIcon,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconCircleCheck, IconBan, IconHelp } from "@tabler/icons-react";
import { TanstackMantineLink } from "@components/tanstack-maintine-link";
// API
import { retrievePlayerQueryOpts } from "@src/services/apiQueries/players";
import { useSuspenseQuery } from "@tanstack/react-query";
// util
import { usePlayerNameDisplay, useFormatDateTime } from "@utils/data-display";

import { PlayerEnabledToggle } from "./player-enabled-toggle";

const route = getRouteApi("/_authenticated/app/players/$playerId");

export function PlayerDetailPage(): React.ReactElement {
  const { t } = useTranslation();

  const { playerId } = route.useLoaderData();
  const { data: player } = useSuspenseQuery(retrievePlayerQueryOpts(playerId));

  const { getPlayerNameDisplay } = usePlayerNameDisplay();
  const { formatDateTime } = useFormatDateTime();

  return (
    <Container>
      <Stack>
        <Box>
          <Title order={2}>
            {t("playerDetails.header", {
              displayName: getPlayerNameDisplay(player),
            })}
          </Title>

          <SimpleGrid cols={{ xs: 1, sm: 2, md: 3 }} mt="md">
            <Box>
              {t("playerDetails.username", { username: player.username })}
            </Box>
            <Box>
              <Group justify="start" gap={4}>
                <Text>
                  {t("playerDetails.name", {
                    name: player.name || t("utils.notSet"),
                  })}
                </Text>

                {player.name ? null : (
                  <Tooltip
                    multiline
                    w={220}
                    label={t("playerDetails.noProfileSurveyCompleted")}
                  >
                    <ThemeIcon variant="transparent" color="yellow">
                      <IconHelp />
                    </ThemeIcon>
                  </Tooltip>
                )}
              </Group>
            </Box>
            <Box>
              <Group justify="start" gap={4}>
                <Text>{t("playerDetails.isActive")}</Text>
                <ThemeIcon
                  variant="transparent"
                  color={player.is_active ? "green" : "red"}
                >
                  {player.is_active ? <IconCircleCheck /> : <IconBan />}
                </ThemeIcon>
              </Group>
            </Box>
            <Box>
              {t("utils.createdAt", {
                dt: formatDateTime(DateTime.fromISO(player.date_added)),
              })}
            </Box>
            <Box>
              {t("utils.updatedAt", {
                dt: formatDateTime(DateTime.fromISO(player.updated_at)),
              })}
            </Box>
            <Box>
              <Trans
                i18nKey="playerDetails.linkToAccount"
                t={t}
                values={{ accountId: player.account }}
                components={{
                  Link: (
                    <TanstackMantineLink
                      to="/app/accounts/$accountId"
                      params={{ accountId: player.account.toString() }}
                    />
                  ),
                }}
              />
            </Box>
          </SimpleGrid>
        </Box>

        <Divider />

        <Box>
          <Title order={2}>{t("playerDetails.actions")}</Title>
        </Box>

        <PlayerEnabledToggle player={player} />
      </Stack>
    </Container>
  );
}
